import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  getCategoriasAdmin,
  getPublicacionAdmin,
  crearPublicacion,
  actualizarPublicacion,
} from '../../services/adminService';
import { useInstitucion } from '../../context/InstitucionContext';
import { useAuth } from '../../context/AuthContext';
import { idsCategoriasEnAlcance } from '../../utils/permisos';
import CategoriaSelect from '../../components/admin/CategoriaSelect';

const AdminPublicacionForm = () => {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  const history = useHistory();
  const { paleta: colors } = useInstitucion();
  const { usuario } = useAuth();

  const [categorias, setCategorias] = useState([]);
  const [form, setForm] = useState({
    categoriaId: '',
    titulo: '',
    descripcion: '',
    fechaPublicacion: '',
    orden: 0,
  });
  const [archivo, setArchivo] = useState(null);
  const [rutaActual, setRutaActual] = useState(null);
  const [loading, setLoading] = useState(esEdicion);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCategoriasAdmin()
      .then(setCategorias)
      .catch(() => setCategorias([]));
  }, []);

  useEffect(() => {
    if (!esEdicion) return;
    getPublicacionAdmin(id)
      .then((pub) => {
        setForm({
          categoriaId: String(pub.categoriaId),
          titulo: pub.titulo,
          descripcion: pub.descripcion || '',
          fechaPublicacion: pub.fechaPublicacion ? pub.fechaPublicacion.substring(0, 10) : '',
          orden: pub.orden,
        });
        setRutaActual(pub.rutaArchivo);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, esEdicion]);

  // Las categorías contenedoras (con subcategorías) solo organizan: no reciben publicaciones.
  // Un publicador además sólo puede elegir categorías dentro de su alcance.
  const idsContenedoras = useMemo(() => {
    const ids = new Set();
    categorias.forEach((c) => {
      if (c.padreId != null) ids.add(c.padreId);
    });
    const alcance = idsCategoriasEnAlcance(usuario, categorias);
    if (alcance) {
      categorias.forEach((c) => {
        if (!alcance.has(c.id)) ids.add(c.id);
      });
    }
    return ids;
  }, [categorias, usuario]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.categoriaId) {
      setError('Selecciona una categoría.');
      return;
    }
    if (!esEdicion && !archivo) {
      setError('Selecciona el archivo PDF.');
      return;
    }

    const fd = new FormData();
    fd.append('CategoriaId', form.categoriaId);
    fd.append('Titulo', form.titulo);
    fd.append('Descripcion', form.descripcion);
    fd.append('Orden', String(form.orden || 0));
    if (form.fechaPublicacion) fd.append('FechaPublicacion', form.fechaPublicacion);
    if (archivo) fd.append('Archivo', archivo);

    setGuardando(true);
    try {
      if (esEdicion) {
        await actualizarPublicacion(id, fd);
      } else {
        await crearPublicacion(fd);
      }
      history.push('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent';

  if (loading) {
    return <div className="p-12 text-center text-gray-400">Cargando...</div>;
  }

  return (
    <div className="max-w-2xl">
      <nav className="mb-6 text-sm">
        <Link to="/admin" className="font-medium hover:opacity-80" style={{ color: colors.ctaText }}>
          ← Volver a publicaciones
        </Link>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {esEdicion ? 'Editar publicación' : 'Nueva publicación'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoría *</label>
          <CategoriaSelect
            categorias={categorias}
            idsDeshabilitados={idsContenedoras}
            placeholder="Selecciona una categoría"
            name="categoriaId"
            value={form.categoriaId}
            onChange={handleChange}
            required
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Las categorías con subcategorías solo organizan; publica en una subcategoría final.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Título *</label>
          <input
            type="text"
            name="titulo"
            value={form.titulo}
            onChange={handleChange}
            required
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
            placeholder="Ej. Boletín Pastoral Vol.12 N.437"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción</label>
          <textarea
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
            rows={3}
            className={inputClass}
            style={{ '--tw-ring-color': colors.primary }}
            placeholder="Breve descripción del documento"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha de publicación</label>
            <input
              type="date"
              name="fechaPublicacion"
              value={form.fechaPublicacion}
              onChange={handleChange}
              className={inputClass}
              style={{ '--tw-ring-color': colors.primary }}
            />
            <p className="text-xs text-gray-400 mt-1">Si se deja vacío, se usa la fecha de hoy.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Orden</label>
            <input
              type="number"
              name="orden"
              value={form.orden}
              onChange={handleChange}
              className={inputClass}
              style={{ '--tw-ring-color': colors.primary }}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Archivo PDF {esEdicion ? '(opcional: reemplaza el actual)' : '*'}
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setArchivo(e.target.files?.[0] || null)}
            className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />
          {esEdicion && rutaActual && (
            <p className="text-xs text-gray-400 mt-1">Archivo actual: {rutaActual}</p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={guardando}
            className="px-5 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            {guardando ? 'Guardando...' : esEdicion ? 'Guardar cambios' : 'Publicar'}
          </button>
          <Link to="/admin" className="px-5 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100">
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminPublicacionForm;
